import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { isOffDay } from '../lib/businessDays'
import { DIGEST_DEFAULTS } from '../lib/digestConfig'

function todayStr() {
  return new Date().toISOString().slice(0, 10)
}

// Returns the ISO timestamp for the start of the previous work day, skipping weekends and holidays.
function prevWorkDayStart() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - 1)
  while (isOffDay(d)) d.setDate(d.getDate() - 1)
  return d.toISOString()
}

function lsKey(userId) {
  return `daily_digest_shown_${userId}`
}

export function shouldShowDailyDigest(userId) {
  if (!userId) return false
  return localStorage.getItem(lsKey(userId)) !== todayStr()
}

export function markDailyDigestShown(userId) {
  if (!userId) return
  localStorage.setItem(lsKey(userId), todayStr())
}

function resolveDigestConfig(orgNotifSettings) {
  const saved = orgNotifSettings?.dailyDigest ?? {}
  const result = {}
  Object.keys(DIGEST_DEFAULTS).forEach(key => {
    result[key] = { ...DIGEST_DEFAULTS[key], ...(saved[key] ?? {}) }
  })
  return result
}

function isVisibleFor(config, key, role) {
  const item = config[key]
  return !!(item?.enabled && (item.roles || []).includes(role))
}

function addDaysStr(isoDate, days) {
  const d = new Date(isoDate + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function isTaskDone(status, needsApproval) {
  return status === 'approved' || (!needsApproval && status === 'complete')
}

function greeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

const PRIORITY_CLS = {
  high:   'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low:    'bg-green-100 text-green-700',
}

function fmtDue(dateStr) {
  const [, m, d] = dateStr.split('-')
  return `${parseInt(m)}/${parseInt(d)}`
}

function fmtDate(d) {
  if (!d) return '—'
  const dt = new Date(d + 'T00:00:00')
  return `${dt.getMonth() + 1}/${dt.getDate()}/${String(dt.getFullYear()).slice(2)}`
}

// ── SVG icons ─────────────────────────────────────────────────────────────────

const Icons = {
  overdue: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  ),
  dueToday: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  dueWeek: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  approval: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  plane: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  ),
  calendar: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  alert: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  check: (
    <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
}

// ── Shared components ─────────────────────────────────────────────────────────

function TaskRow({ task, daysOverdue, showDue, onGoToProject }) {
  const reg = task.projects?.aircraft?.registration || task.projects?.secondary_registration || null
  const projectId = task.projects?.id
  return (
    <div className="flex items-center gap-2 py-1.5">
      <span className={`shrink-0 text-xs font-medium px-1.5 py-0.5 rounded ${PRIORITY_CLS[task.priority] ?? PRIORITY_CLS.medium}`}>
        {task.priority ?? 'med'}
      </span>
      <span className="text-sm text-gray-800 truncate flex-1">
        {task.title}
        {reg ? (
          onGoToProject && projectId
            ? <button onClick={() => onGoToProject(projectId)} className="text-brand-red hover:text-brand-red-dark hover:underline"> · {reg}</button>
            : <span className="text-gray-400"> · {reg}</span>
        ) : null}
      </span>
      {showDue && task.due_date && (
        <span className="shrink-0 text-xs text-brand-red font-medium">({fmtDue(task.due_date)})</span>
      )}
      {daysOverdue != null && (
        <span className="shrink-0 text-xs text-red-500 font-medium">{daysOverdue}d late</span>
      )}
    </div>
  )
}

function Section({ icon, label, count, colorCls, children }) {
  if (count === 0) return null
  return (
    <div>
      <div className={`flex items-center gap-1.5 mb-1 text-xs font-semibold uppercase tracking-wide ${colorCls}`}>
        <span className="shrink-0">{icon}</span>
        <span>{label}</span>
        <span className="ml-auto font-bold">{count}</span>
      </div>
      <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 px-3">
        {children}
      </div>
    </div>
  )
}

function ProjectRow({ project, note, onGoToProject }) {
  const reg = project.aircraft?.registration || project.secondary_registration || '—'
  return (
    <div className="flex items-center gap-0 py-1.5 min-w-0">
      {onGoToProject && project.id
        ? <button onClick={() => onGoToProject(project.id)} className="shrink-0 text-sm font-bold text-brand-red hover:text-brand-red-dark hover:underline">{reg}</button>
        : <span className="shrink-0 text-sm font-bold text-gray-800">{reg}</span>
      }
      {project.workscope && (
        <span className="text-sm text-gray-500 truncate min-w-0"> - {project.workscope}</span>
      )}
      {note && <span className="ml-auto shrink-0 pl-2 text-xs text-amber-600 font-medium">{note}</span>}
    </div>
  )
}

// ── Task data fetcher ─────────────────────────────────────────────────────────

async function fetchDigest(userId, canApprove) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const tStr    = todayStr()
  const weekStr = new Date(today.getTime() + 7 * 86400000).toISOString().slice(0, 10)

  const { data: myRows } = await supabase
    .from('task_assignees')
    .select('task_id, status')
    .eq('user_id', userId)

  const taskIds = (myRows || []).map(r => r.task_id)
  const myMap   = Object.fromEntries((myRows || []).map(r => [r.task_id, r]))

  const { data: tasks } = taskIds.length
    ? await supabase.from('tasks').select('id, title, due_date, priority, needs_approval, projects(id, secondary_registration, aircraft(registration))').in('id', taskIds).is('deleted_at', null)
    : { data: [] }

  const active = (tasks || []).filter(t => {
    const row = myMap[t.id]
    return !isTaskDone(row?.status, t.needs_approval)
  })

  const overdue  = active.filter(t => t.due_date && t.due_date < tStr)
  const dueToday = active.filter(t => t.due_date === tStr)
  const dueWeek  = active.filter(t => t.due_date && t.due_date > tStr && t.due_date <= weekStr)

  let pendingApproval = []
  if (canApprove) {
    const { data: submitted } = await supabase
      .from('task_assignees')
      .select('task_id')
      .eq('status', 'submitted')
    const submittedIds = (submitted || []).map(r => r.task_id)
    if (submittedIds.length) {
      const { data: mine } = await supabase
        .from('tasks')
        .select('id, title, priority')
        .eq('created_by', userId)
        .in('id', submittedIds)
        .is('deleted_at', null)
      pendingApproval = mine || []
    }
  }

  return { overdue, dueToday, dueWeek, pendingApproval }
}

// ── Training digest ───────────────────────────────────────────────────────────

function trainingNextDue(assignment) {
  const type = assignment.training_types
  if (!type?.is_recurring || !type.interval_months) return null
  const completions = (assignment.training_completions || []).filter(c => c.completed_date)
  const last = completions.sort((a, b) => new Date(b.completed_date) - new Date(a.completed_date))[0]
  if (!last) return null
  const d = new Date(last.completed_date + 'T00:00:00')
  d.setMonth(d.getMonth() + type.interval_months)
  return d
}

async function fetchTrainingDigest(withinDays) {
  const { data: personnel } = await supabase
    .from('training_personnel')
    .select(`
      id, first_name, last_name, department,
      training_assignments(
        id, status,
        training_types(id, title, is_recurring, interval_months),
        training_completions(id, completed_date)
      )
    `)
    .eq('is_active', true)

  if (!personnel) return { overdue: [], dueSoon: [] }

  const today = new Date(); today.setHours(0, 0, 0, 0)
  const futureMs = today.getTime() + withinDays * 86400000
  const overdue = [], dueSoon = []

  personnel.forEach(person => {
    const active = (person.training_assignments || []).filter(a => a.status === 'active')
    for (const a of active) {
      const due = trainingNextDue(a)
      if (!due) continue
      const ms = due.getTime()
      if (ms < today.getTime()) {
        overdue.push({ person, assignment: a, due })
        break
      }
      if (ms <= futureMs) dueSoon.push({ person, assignment: a, due })
    }
  })

  return { overdue, dueSoon }
}

function TrainingSection({ data, withinDays }) {
  if (!data || (data.overdue.length === 0 && data.dueSoon.length === 0)) return null
  const fmtDays = (due) => {
    const d = Math.round((due - new Date()) / 86400000)
    return d < 0 ? `${Math.abs(d)}d late` : `${d}d`
  }
  return (
    <>
      {data.overdue.length > 0 && (
        <Section icon={Icons.overdue} label="Training Overdue" count={data.overdue.length} colorCls="text-red-600">
          {data.overdue.map(({ person, assignment }) => (
            <div key={`${person.id}-${assignment.id}`} className="flex items-center gap-2 py-1.5">
              <span className="text-sm font-medium text-gray-800">{person.first_name} {person.last_name}</span>
              <span className="text-xs text-gray-400 truncate flex-1">— {assignment.training_types?.title}</span>
            </div>
          ))}
        </Section>
      )}
      {data.dueSoon.length > 0 && (
        <Section icon={Icons.dueWeek} label={`Training Due (${withinDays}d)`} count={data.dueSoon.length} colorCls="text-amber-600">
          {data.dueSoon.map(({ person, assignment, due }) => (
            <div key={`${person.id}-${assignment.id}`} className="flex items-center gap-2 py-1.5">
              <span className="text-sm font-medium text-gray-800">{person.first_name} {person.last_name}</span>
              <span className="text-xs text-gray-400 truncate flex-1">— {assignment.training_types?.title}</span>
              <span className="shrink-0 text-xs text-amber-600 font-medium">{fmtDays(due)}</span>
            </div>
          ))}
        </Section>
      )}
    </>
  )
}

// ── Schedule tab content per role ─────────────────────────────────────────────

function ManagerScheduleTab({ projects, onGoToProject }) {
  const yesterday = useMemo(() => prevWorkDayStart(), [])
  const today = useMemo(() => todayStr(), [])

  const newProjects = useMemo(
    () => projects.filter(p => p.created_at > yesterday && (!p.arrival_date || p.arrival_date >= today)),
    [projects, yesterday, today]
  )

  const dateChanges = useMemo(
    () => projects.filter(p =>
      (p.arrival_date_changed_at && p.arrival_date_changed_at > yesterday && p.arrival_date >= today) ||
      (p.departure_date_changed_at && p.departure_date_changed_at > yesterday && p.departure_date >= today)
    ),
    [projects, yesterday, today]
  )

  if (newProjects.length === 0 && dateChanges.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="flex justify-center mb-3">{Icons.check}</div>
        <p className="font-medium text-gray-700">No schedule updates</p>
        <p className="text-sm text-gray-400 mt-1">No new projects or date changes since the last business day.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Section icon={Icons.plane} label="New Projects" count={newProjects.length} colorCls="text-brand-red">
        {newProjects.map(p => (
          <ProjectRow key={p.id} project={p} note={`Arrives ${fmtDate(p.arrival_date)}`} onGoToProject={onGoToProject} />
        ))}
      </Section>
      <Section icon={Icons.alert} label="Schedule Changes" count={dateChanges.length} colorCls="text-amber-600">
        {dateChanges.map(p => {
          const parts = []
          if (p.arrival_date_changed_at > yesterday && p.arrival_date >= today) parts.push(`arr → ${fmtDate(p.arrival_date)}`)
          if (p.departure_date_changed_at > yesterday && p.departure_date >= today) parts.push(`dep → ${fmtDate(p.departure_date)}`)
          return <ProjectRow key={p.id} project={p} note={parts.join(' · ')} onGoToProject={onGoToProject} />
        })}
      </Section>
    </div>
  )
}

function TechScheduleTab({ linkedTech, projects, projectTechMap, onGoToProject }) {
  const yesterday = useMemo(() => prevWorkDayStart(), [])
  const today = useMemo(() => todayStr(), [])

  const assignedActiveProjects = useMemo(() => {
    if (!linkedTech) return []
    return projects.filter(p =>
      p.status === 'In Work' &&
      (projectTechMap[p.id] || []).some(t => t.id === linkedTech.id)
    )
  }, [projects, linkedTech, projectTechMap])

  const recentChanges = useMemo(
    () => assignedActiveProjects.filter(p =>
      (p.arrival_date_changed_at && p.arrival_date_changed_at > yesterday && p.arrival_date >= today) ||
      (p.departure_date_changed_at && p.departure_date_changed_at > yesterday && p.departure_date >= today)
    ),
    [assignedActiveProjects, yesterday, today]
  )

  if (assignedActiveProjects.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="flex justify-center mb-3">{Icons.check}</div>
        <p className="font-medium text-gray-700">No active assignments</p>
        <p className="text-sm text-gray-400 mt-1">You have no projects currently in work.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {recentChanges.length > 0 && (
        <Section icon={Icons.alert} label="Date Changes" count={recentChanges.length} colorCls="text-amber-600">
          {recentChanges.map(p => {
            const parts = []
            if (p.arrival_date_changed_at > yesterday && p.arrival_date >= today) parts.push(`arr → ${fmtDate(p.arrival_date)}`)
            if (p.departure_date_changed_at > yesterday && p.departure_date >= today) parts.push(`dep → ${fmtDate(p.departure_date)}`)
            return <ProjectRow key={p.id} project={p} note={parts.join(' · ')} onGoToProject={onGoToProject} />
          })}
        </Section>
      )}
      <Section icon={Icons.plane} label="Your Active Projects" count={assignedActiveProjects.length} colorCls="text-brand-red">
        {assignedActiveProjects.map(p => (
          <ProjectRow key={p.id} project={p}
            note={p.departure_date ? `Dep ${fmtDate(p.departure_date)}` : undefined}
            onGoToProject={onGoToProject} />
        ))}
      </Section>
    </div>
  )
}

function QAScheduleTab({ projects, subtasks, onGoToProject }) {
  const qaProjects = useMemo(() => {
    const activeIds = new Set(
      projects.filter(p => p.status === 'In Work' || p.status === 'Scheduled').map(p => p.id)
    )
    const incoming = new Set()
    const rts      = new Set()
    const other    = new Set()

    subtasks.forEach(s => {
      if (!activeIds.has(s.project_id) || s.department !== 'QA') return
      const name = (s.task_name || '').trim()
      if (name === 'Incoming' || name === 'Incoming/RTS') incoming.add(s.project_id)
      else if (name === 'RTS') rts.add(s.project_id)
      else if (s.status === 'In Work') other.add(s.project_id)
    })

    const byId = Object.fromEntries(projects.map(p => [p.id, p]))
    return {
      incoming: [...incoming].map(id => byId[id]).filter(Boolean),
      rts:      [...rts].filter(id => !incoming.has(id)).map(id => byId[id]).filter(Boolean),
      other:    [...other].filter(id => !incoming.has(id) && !rts.has(id)).map(id => byId[id]).filter(Boolean),
    }
  }, [projects, subtasks])

  const total = qaProjects.incoming.length + qaProjects.rts.length + qaProjects.other.length

  if (total === 0) {
    return (
      <div className="text-center py-8">
        <div className="flex justify-center mb-3">{Icons.check}</div>
        <p className="font-medium text-gray-700">No QA attention needed</p>
        <p className="text-sm text-gray-400 mt-1">No projects currently require QA action.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Section icon={Icons.plane} label="Incoming" count={qaProjects.incoming.length} colorCls="text-brand-red">
        {qaProjects.incoming.map(p => <ProjectRow key={p.id} project={p} onGoToProject={onGoToProject} />)}
      </Section>
      <Section icon={Icons.alert} label="RTS" count={qaProjects.rts.length} colorCls="text-green-600">
        {qaProjects.rts.map(p => <ProjectRow key={p.id} project={p} onGoToProject={onGoToProject} />)}
      </Section>
      <Section icon={Icons.calendar} label="In QA Milestone" count={qaProjects.other.length} colorCls="text-purple-600">
        {qaProjects.other.map(p => <ProjectRow key={p.id} project={p} onGoToProject={onGoToProject} />)}
      </Section>
    </div>
  )
}

// ── Modal ─────────────────────────────────────────────────────────────────────

export default function DailyDigestModal({ onClose, onGoToTasks, onGoToProject, dashboardRole, isTechnician, linkedTech, projects = [], subtasks = [], projectTechMap = {} }) {
  const { user, profile, isEditor, isSubtaskEditor } = useAuth()
  const canApprove = isEditor || isSubtaskEditor

  const digestConfig = useMemo(() => resolveDigestConfig(profile?.orgs?.settings?.notifications), [profile?.orgs?.id])
  const userRole = profile?.role ?? 'technician'

  const [activeTab,    setActiveTab]    = useState('schedule')
  const [taskLoading,  setTaskLoading]  = useState(true)
  const [taskData,     setTaskData]     = useState(null)
  const [trainingData, setTrainingData] = useState(null)

  const displayName = profile?.display_name || user?.email?.split('@')[0] || ''

  useEffect(() => {
    if (!user) return
    fetchDigest(user.id, canApprove).then(d => {
      setTaskData(d)
      setTaskLoading(false)
    })
  }, [user?.id, canApprove])

  useEffect(() => {
    if (!isVisibleFor(digestConfig, 'training', userRole)) return
    fetchTrainingDigest(digestConfig.training.withinDays ?? 30).then(setTrainingData)
  }, [digestConfig, userRole])

  const dismiss = () => {
    markDailyDigestShown(user?.id)
    onClose()
  }

  const taskCount = taskData
    ? taskData.overdue.length + taskData.dueToday.length + taskData.pendingApproval.length
    : 0

  const showScheduleTab = !isTechnician && dashboardRole && dashboardRole !== 'hr'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={e => e.target === e.currentTarget && dismiss()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold text-gray-900 text-base">
                {greeting()}{displayName ? `, ${displayName}` : ''}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <button onClick={dismiss} className="text-gray-300 hover:text-gray-500 text-xl leading-none mt-0.5">&times;</button>
          </div>

          {/* Tabs */}
          {showScheduleTab && (
            <div className="flex gap-1 mt-3">
              <button
                onClick={() => setActiveTab('schedule')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  activeTab === 'schedule'
                    ? 'bg-brand-black text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                Schedule
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors relative ${
                  activeTab === 'tasks'
                    ? 'bg-brand-black text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                Tasks
                {taskCount > 0 && (
                  <span className={`ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                    activeTab === 'tasks' ? 'bg-white text-brand-black' : 'bg-brand-red text-white'
                  }`}>
                    {taskCount}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

          {/* Schedule tab — config-driven sections */}
          {showScheduleTab && activeTab === 'schedule' && (() => {
            const today = todayStr()

            const overdueItems = isVisibleFor(digestConfig, 'overdue', userRole)
              ? projects.filter(p => p.departure_date && p.departure_date < today && !['Complete', 'TBD'].includes(p.status))
              : []

            const arrivalItems = isVisibleFor(digestConfig, 'arrivals', userRole)
              ? projects.filter(p => p.arrival_date >= today && p.arrival_date <= addDaysStr(today, digestConfig.arrivals.withinDays ?? 7) && !['Complete', 'TBD'].includes(p.status))
              : []

            const deliveryItems = isVisibleFor(digestConfig, 'deliveries', userRole)
              ? projects.filter(p => p.departure_date >= today && p.departure_date <= addDaysStr(today, digestConfig.deliveries.withinDays ?? 7) && !['Complete', 'TBD'].includes(p.status))
              : []

            const showNewProjects = isVisibleFor(digestConfig, 'new_projects', userRole) && dashboardRole === 'manager'
            const showMyTech      = isVisibleFor(digestConfig, 'my_projects', userRole)  && dashboardRole === 'tech'
            const showMyQA        = isVisibleFor(digestConfig, 'my_projects', userRole)  && dashboardRole === 'qa'
            const showTraining    = isVisibleFor(digestConfig, 'training', userRole)

            const anySection = overdueItems.length > 0 || arrivalItems.length > 0 || deliveryItems.length > 0
              || showNewProjects || showMyTech || showMyQA || showTraining

            if (!anySection) {
              return (
                <div className="text-center py-8">
                  <div className="flex justify-center mb-3">{Icons.check}</div>
                  <p className="font-medium text-gray-700">All clear!</p>
                  <p className="text-sm text-gray-400 mt-1">Nothing scheduled to show for your role today.</p>
                </div>
              )
            }

            return (
              <>
                {overdueItems.length > 0 && (
                  <Section icon={Icons.overdue} label="Overdue" count={overdueItems.length} colorCls="text-red-600">
                    {overdueItems.map(p => (
                      <ProjectRow key={p.id} project={p} note={p.departure_date ? `was ${fmtDate(p.departure_date)}` : undefined} onGoToProject={onGoToProject} />
                    ))}
                  </Section>
                )}

                {arrivalItems.length > 0 && (
                  <Section icon={Icons.plane} label={`Arrivals (${digestConfig.arrivals.withinDays ?? 7}d)`} count={arrivalItems.length} colorCls="text-brand-red">
                    {arrivalItems.map(p => (
                      <ProjectRow key={p.id} project={p} note={`Arr ${fmtDate(p.arrival_date)}`} onGoToProject={onGoToProject} />
                    ))}
                  </Section>
                )}

                {deliveryItems.length > 0 && (
                  <Section icon={Icons.calendar} label={`Deliveries (${digestConfig.deliveries.withinDays ?? 7}d)`} count={deliveryItems.length} colorCls="text-green-600">
                    {deliveryItems.map(p => (
                      <ProjectRow key={p.id} project={p} note={`Dep ${fmtDate(p.departure_date)}`} onGoToProject={onGoToProject} />
                    ))}
                  </Section>
                )}

                {showNewProjects && (
                  <ManagerScheduleTab projects={projects} onGoToProject={onGoToProject} />
                )}
                {showMyTech && (
                  <TechScheduleTab linkedTech={linkedTech} projects={projects} projectTechMap={projectTechMap} onGoToProject={onGoToProject} />
                )}
                {showMyQA && (
                  <QAScheduleTab projects={projects} subtasks={subtasks} onGoToProject={onGoToProject} />
                )}

                {showTraining && (
                  <TrainingSection data={trainingData} withinDays={digestConfig.training.withinDays ?? 30} />
                )}
              </>
            )
          })()}

          {/* Tasks tab */}
          {(!showScheduleTab || activeTab === 'tasks') && (
            <TasksTabContent loading={taskLoading} data={taskData} canApprove={canApprove} onGoToProject={onGoToProject} />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400">Won't show again until tomorrow</p>
          <div className="flex items-center gap-2">
            {onGoToTasks && (
              <button
                onClick={onGoToTasks}
                className="text-sm font-medium text-brand-red hover:text-brand-red-dark transition-colors"
              >
                View Tasks →
              </button>
            )}
            <button
              onClick={dismiss}
              className="text-sm font-medium text-white bg-brand-red hover:bg-brand-red-dark rounded-lg px-4 py-2 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function TasksTabContent({ loading, data, canApprove, onGoToProject }) {
  const total = data
    ? data.overdue.length + data.dueToday.length + data.dueWeek.length + data.pendingApproval.length
    : 0

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    )
  }

  if (total === 0) {
    return (
      <div className="text-center py-8">
        <div className="flex justify-center mb-3">{Icons.check}</div>
        <p className="font-medium text-gray-700">You're all caught up!</p>
        <p className="text-sm text-gray-400 mt-1">No tasks overdue, due today, or pending approval.</p>
      </div>
    )
  }

  return (
    <>
      <Section icon={Icons.overdue} label="Overdue" count={data.overdue.length} colorCls="text-red-600">
        {data.overdue.map(t => {
          const days = Math.floor((new Date() - new Date(t.due_date + 'T00:00:00')) / 86400000)
          return <TaskRow key={t.id} task={t} daysOverdue={days} onGoToProject={onGoToProject} />
        })}
      </Section>

      <Section icon={Icons.dueToday} label="Due Today" count={data.dueToday.length} colorCls="text-orange-600">
        {data.dueToday.map(t => <TaskRow key={t.id} task={t} onGoToProject={onGoToProject} />)}
      </Section>

      <Section icon={Icons.dueWeek} label="Due This Week" count={data.dueWeek.length} colorCls="text-brand-red">
        {data.dueWeek.map(t => <TaskRow key={t.id} task={t} showDue onGoToProject={onGoToProject} />)}
      </Section>

      {canApprove && (
        <Section icon={Icons.approval} label="Pending Your Approval" count={data.pendingApproval.length} colorCls="text-purple-600">
          {data.pendingApproval.map(t => <TaskRow key={t.id} task={t} onGoToProject={onGoToProject} />)}
        </Section>
      )}
    </>
  )
}
